import { useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { allPlants } from '../../plants';
import type { Difficulty } from '../../engine/engine';
import { bestFor } from './bests';

const DIFFICULTIES: { id: Difficulty; name: string; blurb: string }[] = [
  { id: 'novice', name: 'Novice', blurb: 'Clean instruments, faults announced, hints shown.' },
  { id: 'operator', name: 'Operator', blurb: 'Realistic noise and drift. Faults arrive without warning.' },
  { id: 'senior', name: 'Senior', blurb: 'Instrument failures, simultaneous events, no hints, no announcements.' },
];

export function StartMenu({ onClose }: { onClose(): void }): JSX.Element {
  const boot = useEngine((s) => s.boot);
  const current = useEngine((s) => s.plant);
  const [plantId, setPlantId] = useState(current?.id ?? allPlants[0]!.id);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [lessonId, setLessonId] = useState<string | null>(null);
  const [shiftHours, setShiftHours] = useState<number | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>(useEngine.getState().difficulty);
  const [seed, setSeed] = useState(String(useEngine.getState().seed || 20_240_101));

  const plant = allPlants.find((p) => p.id === plantId) ?? allPlants[0]!;
  const campaign = plant.scenarios.filter((s) => s.category !== 'training' && s.category !== 'tuning');
  const challenges = plant.scenarios.filter((s) => s.category === 'tuning');

  const start = (): void => {
    const n = Number(seed);
    boot({
      plantId,
      scenarioId: shiftHours ? null : scenarioId,
      lessonId: shiftHours ? null : lessonId,
      seed: Number.isFinite(n) && n > 0 ? Math.floor(n) : 20_240_101,
      difficulty,
      ...(shiftHours ? { shiftModeHours: shiftHours } : {}),
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-deep)',
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      <header style={{ padding: '18px 26px 6px', display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <h1 style={{ margin: 0, fontSize: 21, letterSpacing: 6, fontWeight: 400 }}>SETPOINT</h1>
        <span className="muted">Process control simulator</span>
        <span style={{ flex: 1 }} />
        <button onClick={onClose}>Back to the plant</button>
      </header>

      <div className="scroll" style={{ padding: '10px 26px 26px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <section>
          <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>PLANT</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
            {allPlants.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  setPlantId(p.id);
                  setScenarioId(null);
                  setLessonId(null);
                }}
                style={{
                  textAlign: 'left',
                  padding: '12px 14px',
                  background: plantId === p.id ? 'var(--panel-2)' : 'var(--panel)',
                  borderColor: plantId === p.id ? 'var(--accent)' : 'var(--line-dim)',
                  borderWidth: plantId === p.id ? 2 : 1,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
                  {p.description}
                </div>
                <div className="muted num" style={{ fontSize: 10.5, marginTop: 6 }}>
                  {p.loops.length} loops · {p.alarms.length} alarms · {p.interlocks.length} interlocks ·{' '}
                  {p.scenarios.length} scenarios
                </div>
              </button>
            ))}
          </div>
        </section>

        {(plant.lessons ?? []).length > 0 && (
          <section>
            <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>TRAINING</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
              {(plant.lessons ?? []).map((l) => (
                <button
                  key={l.id}
                  onClick={() => {
                    setLessonId(lessonId === l.id ? null : l.id);
                    setScenarioId(l.scenarioId);
                  }}
                  style={{
                    textAlign: 'left',
                    padding: '9px 12px',
                    background: lessonId === l.id ? 'var(--panel-2)' : 'var(--panel)',
                    borderColor: lessonId === l.id ? 'var(--accent)' : 'var(--line-dim)',
                    borderWidth: lessonId === l.id ? 2 : 1,
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 12 }}>
                    {l.number}. {l.title}
                  </div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
                    {l.summary}
                  </div>
                </button>
              ))}
            </div>
            {lessonId && (
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                The lesson runs alongside the shift you choose below. Click it again to turn it off.
              </div>
            )}
          </section>
        )}

        <section>
          <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>SHIFT</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
            <button
              onClick={() => setScenarioId(null)}
              style={{
                textAlign: 'left',
                padding: '11px 13px',
                background: scenarioId === null ? 'var(--panel-2)' : 'var(--panel)',
                borderColor: scenarioId === null ? 'var(--accent)' : 'var(--line-dim)',
                borderWidth: scenarioId === null ? 2 : 1,
              }}
            >
              <div style={{ fontWeight: 600 }}>Sandbox — free running</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.45 }}>
                No objectives, no clock. Everything unlocked, including the fault-injection panel.
                The place to learn the board and to practise tuning.
              </div>
            </button>
            {campaign.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setScenarioId(s.id);
                  setShiftHours(null);
                }}
                style={{
                  textAlign: 'left',
                  padding: '11px 13px',
                  background: scenarioId === s.id ? 'var(--panel-2)' : 'var(--panel)',
                  borderColor: scenarioId === s.id ? 'var(--accent)' : 'var(--line-dim)',
                  borderWidth: scenarioId === s.id ? 2 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{s.name}</span>
                  <span className="muted num" style={{ fontSize: 10.5 }}>
                    {(s.durationS / 3600).toFixed(0)} h
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.45 }}>
                  {s.brief.length > 190 ? `${s.brief.slice(0, 190)}…` : s.brief}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>SHIFT MODE</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {[4, 8, 12].map((h) => (
              <button
                key={h}
                className={shiftHours === h ? 'on' : ''}
                onClick={() => {
                  setShiftHours(shiftHours === h ? null : h);
                  setScenarioId(null);
                  setLessonId(null);
                }}
              >
                {h}-hour shift
              </button>
            ))}
            <span className="muted" style={{ fontSize: 11.5, maxWidth: 520, lineHeight: 1.45 }}>
              An endless shift built from this plant’s own fault library and your seed. Things go
              wrong at intervals that shorten as the shift wears on, and eventually overlap. Nobody
              tells you what to do about them.
            </span>
          </div>
        </section>

        {challenges.length > 0 && (
          <section>
            <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>TUNING CHALLENGES</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 8 }}>
              {challenges.map((c) => {
                const best = bestFor(plant.id, c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      setScenarioId(c.id);
                      setShiftHours(null);
                      setLessonId(null);
                    }}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      background: scenarioId === c.id ? 'var(--panel-2)' : 'var(--panel)',
                      borderColor: scenarioId === c.id ? 'var(--accent)' : 'var(--line-dim)',
                      borderWidth: scenarioId === c.id ? 2 : 1,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{c.name}</div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.45 }}>
                      {c.brief.slice(0, 150)}…
                    </div>
                    <div className="muted num" style={{ fontSize: 10.5, marginTop: 5 }}>
                      target IAE ≤ {c.challengeTarget}
                      {best?.iae !== undefined ? ` · your best ${best.iae.toFixed(0)}` : ' · not attempted'}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>DIFFICULTY</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 380 }}>
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDifficulty(d.id)}
                  style={{
                    textAlign: 'left',
                    padding: '8px 11px',
                    background: difficulty === d.id ? 'var(--panel-2)' : 'var(--panel)',
                    borderColor: difficulty === d.id ? 'var(--accent)' : 'var(--line-dim)',
                  }}
                >
                  <strong style={{ fontSize: 12 }}>{d.name}</strong>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {d.blurb}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h2 style={{ fontSize: 13, margin: '0 0 8px', letterSpacing: 1.5 }}>SEED</h2>
            <input value={seed} onChange={(e) => setSeed(e.target.value)} style={{ width: 160 }} />
            <div className="muted" style={{ fontSize: 11, marginTop: 6, maxWidth: 300, lineHeight: 1.45 }}>
              The same seed replays the same shift: the same noise, the same disturbances, the
              same everything. Share it to compare like for like.
            </div>
            <button style={{ marginTop: 8 }} onClick={() => setSeed(String(Math.floor(Math.random() * 9_000_000) + 1_000_000))}>
              New seed
            </button>
          </div>
        </section>

        <div>
          <button className="on" onClick={start} style={{ padding: '10px 26px', fontSize: 14 }}>
            Take the shift
          </button>
        </div>
      </div>
    </div>
  );
}
